// SPDX-License-Identifier: BUSL-1.1

/**
 * Runtime attestation + heartbeat staleness chips for the agent-detail page.
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 *
 * PIO-31: runtime attestation indicator. Source-of-truth SHA is
 * not yet plumbed end-to-end (the worker would need to fetch the
 * latest cli-ceo-agents/main HEAD). For now we pass null so the
 * indicator renders the running SHA + booted-age without a verdict.
 * Once PIO-32 lands the preflight endpoint, expectedSha will come
 * from there. The HeartbeatStalenessChip mirrors the HB-04
 * watchdog's thresholds (NocoDB #4568) so an operator looking at
 * this page sees the same severity buckets the server-side
 * watchdog uses to emit blocker rows + STALL_WEBHOOK_URL POSTs.
 */

import { AttestationIndicator } from '@/components/attestation-indicator';
import { HeartbeatStalenessChip } from '@/components/heartbeat-staleness-chip';

/**
 * Canonical capability flags on `ops_agents.capabilities` (JSONB, defaults
 * to {sessions:false, jobs:false, budget:true, policy:true, rateLimit:false,
 * approval:false}). Mirrors the runner-side `AGENT_CAPABILITIES` contract in
 * `cli-ceo-agents/packages/pr-reviewer-runner/src/runner-simple.ts` — adding
 * a new key here requires extending the runner's registration payload too.
 */
const CAPABILITY_KEYS = [
  'sessions',
  'jobs',
  'budget',
  'policy',
  'rateLimit',
  'approval',
] as const;
type CapabilityKey = (typeof CAPABILITY_KEYS)[number];
type Capabilities = Partial<Record<CapabilityKey, boolean>>;

/**
 * Compact capability-matrix chip row. Per-flag chip is emerald when enabled,
 * neutral + struck-through when disabled. Operators answer "what is this
 * agent built to do?" without poking at the raw JSONB column.
 *
 * Mirrors the operator-console mental model: attestation answers "what is it
 * running?", capabilities answer "what is it allowed / expected to do?".
 */
function CapabilityChips({ capabilities }: { capabilities: Capabilities }) {
  return (
    <div
      className="flex flex-wrap items-center gap-1"
      role="group"
      aria-label="Agent capability matrix"
    >
      <span className="mr-1 text-[10px] uppercase tracking-wide text-neutral-500">
        capabilities
      </span>
      {CAPABILITY_KEYS.map((key) => {
        const enabled = capabilities?.[key] === true;
        return (
          <span
            key={key}
            title={
              enabled
                ? `${key}: registered + enabled`
                : `${key}: not advertised by this agent`
            }
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] ${
              enabled
                ? 'bg-emerald-900/40 text-emerald-300'
                : 'bg-neutral-800/60 text-neutral-500 line-through'
            }`}
          >
            <span aria-hidden>{enabled ? '✓' : '✕'}</span>
            {key}
          </span>
        );
      })}
    </div>
  );
}

export function AgentDetailMetadataPanel({
  agentData,
  agentId,
}: {
  agentData: unknown;
  agentId: string | undefined;
}) {
  const attestation =
    (agentData as { attestation?: unknown })?.attestation ?? null;
  const lastHeartbeat =
    (agentData as { lastHeartbeat?: string | null })?.lastHeartbeat ?? null;
  const capabilities =
    (agentData as { capabilities?: Capabilities | null })?.capabilities ?? null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <AttestationIndicator
        attestation={attestation as never}
        expectedSha={null}
        agentId={agentId}
      />
      <HeartbeatStalenessChip lastHeartbeat={lastHeartbeat} />
      {capabilities && <CapabilityChips capabilities={capabilities} />}
    </div>
  );
}
